import { FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useInstrumentApi } from '../../hooks/useInstrumentApi'
import {
  filterInstrumentName,
  filterMarketName,
  searchAllName,
  filterInstrumentIsin,
  filterInstrumentAssetClass,
  filterInstrumentCurrency,
  filterInstrumentTicker,
  filterInstrumentCategory,
  filterInstrumentExperience,
  filterInstrumentPreference,
  filterInstrumentLossAbility,
  filterInstrumentRisk,
  filterInstrumentHorizonte,
} from '../../redux/actions/filterActions'
import { useSelector } from '../../redux/hooks/useSelector'
import { ColoredIconButton } from '../common/ColoredIconButton'
import AddIcon from '@mui/icons-material/AddRounded'
import PublishIcon from '@mui/icons-material/PublishRounded'
import { FILE_IMPORT_DIALOG_ID, INSTRUMENT_INPUT_DIALOG_ID } from '../../constants/dialogConstants'
import { openDialog } from '../../redux/actions/dialogActions'
import { getSecurityAction } from '../../redux/actions/instrumentActions'
import { useTranslation } from 'react-i18next'
import CardWrapper from '../common/CardWrapper'
import { useDebounce } from '../../utils/bouncing'
import { tokens } from '../../styles/tokens'
import { Security } from '../../redux/reducers/securities'
import { CATEGORY, CURRENCY, EXPERIENCE, HORIZONTE, ISIN, LOSS_ABILITY, MARKET, PREFERENCE, RISK, TICKER, TYPE } from '../../constants/textConstants'

const useStyles = makeStyles(theme => ({
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    margin: '16px 0',
  },
  primaryRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  secondaryRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderTop: `1px solid ${tokens.color.neutral[100]}`,
    paddingTop: theme.spacing(1),
  },
  instrument: {
    marginLeft: theme.spacing(0.5),
  },
  market: {
    marginLeft: theme.spacing(1),
  },
  searchAll: {
    marginLeft: theme.spacing(1),
  },
  compactSelect: {
    minWidth: 110,
  },
  addButton: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  icon: {
    alignSelf: 'center',
  },
}))

interface Props {
  setIsUpdate: (params: any) => any
  setIsDuplicate: (params: any) => any
}

export const InstrumentTableFilters: React.FC<Props> = ({ setIsUpdate, setIsDuplicate }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const filters = useSelector(state => state.filters)
  const securities: Security[] = useSelector(state => state.securities.securities)
  const { getInstrumentsByParam } = useInstrumentApi()
  const { t } = useTranslation()

  const onNameSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterInstrumentName(event.target.value))
  }

  const onSearchAllChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    dispatch(searchAllName(checked))
  }

  const debouncedSearchTerm = useDebounce(filters.searchedInstrumentName, 300);

  useEffect(() => {
    getInstrumentsByParam(
      debouncedSearchTerm.length >= 3 ? debouncedSearchTerm : '',
      filters.searchedAll
    )
  }, [debouncedSearchTerm, filters.searchedAll])

  const uniqueValues = (getter: (security: Security) => string | number | null | undefined): string[] => {
    if (!Array.isArray(securities)) return []
    const set = new Set<string>()
    for (const security of securities) {
      const value = getter(security)
      if (value !== null && value !== undefined && String(value) !== '') set.add(String(value))
    }
    return Array.from(set).sort()
  }

  const assetClassOptions = uniqueValues(security => security.assetClass)
  const marketOptions = uniqueValues(security => security.market)
  const currencyOptions = uniqueValues(security => security.currency)
  const categoryOptions = uniqueValues(security => security.category)
  const experienceOptions = uniqueValues(security => security.skillAndExperience)
  const preferenceOptions = uniqueValues(security => security.preference)
  const lossAbilityOptions = uniqueValues(security => security.lossAbility)
  const riskOptions = uniqueValues(security => security.riskValue)
  const horizonteOptions = uniqueValues(security => security.horizonteValue)

  return (
    <CardWrapper className={classes.filterContainer}>
      <div className={classes.primaryRow}>
        <div>
          <TextField
            className={classes.instrument}
            placeholder={t('INSTRUMENT')}
            onChange={onNameSearchFieldChange}
            value={filters.searchedInstrumentName}
            variant="standard"
          />
        </div>
        <div>
          <FormControlLabel
            className={classes.searchAll}
            control={<Switch checked={filters.searchedAll} onChange={onSearchAllChange} />}
            label={t('INSTRUMENT_WITHOUT_TARGET_MARKET')}
          />
        </div>
        <div className={classes.addButton}>
          <ColoredIconButton
            icon={<PublishIcon />}
            size="small"
            className={classes.icon}
            title={t('FILE_IMPORT')}
            onClick={() => {
              dispatch(openDialog(FILE_IMPORT_DIALOG_ID))
            }}
          />
          <ColoredIconButton
            icon={<AddIcon />}
            size="small"
            className={classes.icon}
            title={t('NEW_INSTRUMENT')}
            onClick={() => {
              dispatch(getSecurityAction(undefined))
              setIsUpdate(false)
              setIsDuplicate(false)
              dispatch(
                openDialog(INSTRUMENT_INPUT_DIALOG_ID, {
                  dialogTitle: t('NEW_INSTRUMENT'),
                  type: 'new',
                })
              )
            }}
          />
        </div>
      </div>
      <div className={classes.secondaryRow}>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(TYPE)}</InputLabel>
          <Select
            value={filters.searchedInstrumentAssetClass}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentAssetClass(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {assetClassOptions.map(value => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.compactSelect}
          label={t(ISIN)}
          value={filters.searchedInstrumentIsin}
          onChange={(event: React.ChangeEvent<{ value: string }>) => dispatch(filterInstrumentIsin(event.target.value))}
          variant="standard"
        />
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(MARKET)}</InputLabel>
          <Select
            value={filters.searchedMarketName}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterMarketName(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {marketOptions.map(value => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(CURRENCY)}</InputLabel>
          <Select
            value={filters.searchedInstrumentCurrency}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentCurrency(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {currencyOptions.map(value => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.compactSelect}
          label={t(TICKER)}
          value={filters.searchedInstrumentTicker}
          onChange={(event: React.ChangeEvent<{ value: string }>) => dispatch(filterInstrumentTicker(event.target.value))}
          variant="standard"
        />
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(CATEGORY)}</InputLabel>
          <Select
            value={filters.searchedInstrumentCategory}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentCategory(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {categoryOptions.map(value => (
              <MenuItem key={value} value={value}>{t(value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(EXPERIENCE)}</InputLabel>
          <Select
            value={filters.searchedInstrumentExperience}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentExperience(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {experienceOptions.map(value => (
              <MenuItem key={value} value={value}>{t(value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(PREFERENCE)}</InputLabel>
          <Select
            value={filters.searchedInstrumentPreference}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentPreference(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {preferenceOptions.map(value => (
              <MenuItem key={value} value={value}>{t(value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(LOSS_ABILITY)}</InputLabel>
          <Select
            value={filters.searchedInstrumentLossAbility}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentLossAbility(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {lossAbilityOptions.map(value => (
              <MenuItem key={value} value={value}>{t(value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(RISK)}</InputLabel>
          <Select
            value={filters.searchedInstrumentRisk}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentRisk(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {riskOptions.map(value => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel shrink>{t(HORIZONTE)}</InputLabel>
          <Select
            value={filters.searchedInstrumentHorizonte}
            onChange={(event: SelectChangeEvent<string>) => dispatch(filterInstrumentHorizonte(event.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {horizonteOptions.map(value => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </CardWrapper>
  )
}
